import React from 'react';
import { property } from 'lodash-es';
import { getDisplayDate } from '../Context/visitLogUtils';
import {
  effectiveDateValueGetter,
  largeProviderValueGetter,
  smallProviderValueGetter,
  patientFirstNameValueGetter,
  patientLastNameValueGetter,
  durationValueGetter,
  lateChartingValueGetter,
  lateChartingTooltipValueGetter,
  roleCodeGetter,
  typeCodeGetter,
  locCodeGetter,
  locationCodeGetter,
  selectedAttributesExportValueGetter,
} from './valueGetters';
import {
  DotRenderer,
  CountCellRenderer,
  HCPCSCellRenderer,
  statusCellRenderer,
  HeaderCellRenderer,
  DistanceRenderer,
  hopeStatusCellRenderer,
  electionAddendumStatusCellRenderer,
} from './CellRenderer';
import VisitLogDetailRow from './DetailRow/VisitLogDetailRow';
import {
  M as m,
  F as f,
  N as n,
  T as t,
  H as h,
  F2F as f2f,
} from '../Context/constants';
import {
  visitDate,
  visitProvider,
  visitType,
  visitDuration,
  lateChart,
  VisitAttributes as VISITATTRIBUTES,
  firstName,
  lastName,
  ID,
  personID,
  roleName,
  visitID,
  faceToFaceVisit,
  arriveDistance,
  departDistance,
  roleCode,
  typeCode,
  locCode,
  locationCode,
  createdDate,
} from '../../translations/en.json';

export const commonColumnDefs = {
  wrapText: true,
  sortable: true,
  filter: true,
  autoHeight: true,
  flex: 1,
  cellStyle: {
    display: 'inline-block',
    position: 'absolute',
    overflow: 'ellipsis',
  },
};

const iconCellStyle = {
  overflow: 'visible',
  borderRight: 'solid grey 2px',
};

export const detailRowDefs = {
  detailCellRendererParams: {
    detailGridOptions: {
      suppressLoadingOverlay: true,
    },
  },
};

const VisitAttributes = {
  headerName: VISITATTRIBUTES,
  field: VISITATTRIBUTES,
  valueGetter: selectedAttributesExportValueGetter,
  cellRendererFramework: DotRenderer,
  flex: 0.5,
  hide: true,
  minWidth: 75,
};

const createdDateField = 'data.starteventdatetime_shifted';

const CreatedDate = {
  headerName: createdDate,
  valueGetter: property(createdDateField),
  valueFormatter: getDisplayDate(createdDateField),
  flex: 0.5,
  minWidth: 130,
};

const VisitDate = {
  headerName: visitDate,
  valueGetter: effectiveDateValueGetter,
  flex: 0.5,
  minWidth: 130,
};

const VisitStatus = {
  headerName: '',
  cellRendererFramework: statusCellRenderer,
  field: 'status',
  flex: 0.1,
  minWidth: 38,
};

const VisitProviderSmall = {
  headerName: visitProvider,
  valueGetter: smallProviderValueGetter,
  flex: 0.3,
  minWidth: 85,
};

const VisitProviderLarge = {
  headerName: visitProvider,
  valueGetter: largeProviderValueGetter,
  flex: 1,
  minWidth: 130,
};

const VisitProviderPersonId = {
  headerName: personID,
  flex: 0.4,
  minWidth: 75,
  field: 'personnumber',
  hide: true,
};

const VisitProviderRoleName = {
  headerName: roleName,
  flex: 0.2,
  minWidth: 70,
  field: 'rolename',
  hide: true,
};

const VisitId = {
  headerName: visitID,
  flex: 0.4,
  minWidth: 75,
  field: 'id',
  hide: true,
};

const PrintF2F = {
  headerName: faceToFaceVisit,
  flex: 0.4,
  minWidth: 75,
  field: 'facetofacevisit',
  hide: true,
};

const VisitPatientFirstNameSmall = {
  headerName: firstName,
  valueGetter: patientFirstNameValueGetter,
  flex: 0.4,
  minWidth: 85,
};

const VisitPatientLastNameSmall = {
  headerName: lastName,
  valueGetter: patientLastNameValueGetter,
  flex: 0.4,
  minWidth: 85,
};

const VisitPatientFirstNameLarge = {
  headerName: firstName,
  valueGetter: patientFirstNameValueGetter,
  flex: 0.45,
  minWidth: 130,
};

const VisitPatientLastNameLarge = {
  headerName: lastName,
  valueGetter: patientLastNameValueGetter,
  flex: 0.45,
  minWidth: 130,
};

const VisitPatientIDLarge = {
  headerName: ID,
  flex: 0.2,
  minWidth: 70,
  field: 'patientnumber',
};

const VisitPatientIDSmall = {
  headerName: ID,
  field: 'patientnumber',
  flex: 0.2,
  minWidth: 70,
};

const VisitType = {
  headerName: visitType,
  field: 'compiledvisittype',
  flex: 0.25,
  minWidth: 145,
};

const Hope = {
  headerName: 'HOPE',
  cellRendererFramework: hopeStatusCellRenderer,
  field: '',
  resizable: true,
  // flex: 0.2,
  minWidth: 100,
};

const ElectionAddendum = {
  headerName: 'EA',
  cellRendererFramework: electionAddendumStatusCellRenderer,
  field: '',
  resizable: true,
  minWidth: 50,
};

const RoleCode = {
  headerName: roleCode,
  valueGetter: roleCodeGetter,
  flex: 0.4,
  minWidth: 75,
  hide: true,
};

const TypeCode = {
  headerName: typeCode,
  valueGetter: typeCodeGetter,
  flex: 0.4,
  minWidth: 75,
  hide: true,
};

const LOCCode = {
  headerName: locCode,
  valueGetter: locCodeGetter,
  flex: 0.4,
  minWidth: 75,
  hide: true,
};

const LocationCode = {
  headerName: locationCode,
  valueGetter: locationCodeGetter,
  flex: 0.4,
  minWidth: 75,
  hide: true,
};

const ArriveDistance = {
  headerName: '',
  field: arriveDistance,
  headerComponent: 'ColumnIcon',
  valueGetter: ({ data: { arrivedistancemiles, mileage } }: any) =>
    arrivedistancemiles || mileage,
  cellRendererFramework: DistanceRenderer,
  flex: 0.1,
  minWidth: 40,
  suppressMenu: true,
};

const DepartDistance = {
  headerName: '',
  field: departDistance,
  headerComponent: 'ColumnIcon',
  valueGetter: ({ data: { departdistancemiles, mileage } }: any) =>
    departdistancemiles || mileage,
  cellRendererFramework: DistanceRenderer,
  flex: 0.1,
  minWidth: 40,
  suppressMenu: true,
};

const VisitDuration = {
  headerName: visitDuration,
  valueGetter: durationValueGetter,
  flex: 0.2,
  minWidth: 75,
};

const LateChart = {
  headerName: lateChart,
  valueGetter: lateChartingValueGetter,
  tooltipValueGetter: lateChartingTooltipValueGetter,
  flex: 0.2,
  minWidth: 78,
};

const F2F = {
  headerName: '',
  field: f2f,
  headerComponent: 'ColumnIcon',
  cellRendererFramework: CountCellRenderer,
  cellStyle: iconCellStyle,
  flex: 0.12,
  minWidth: 30,
};

const M = {
  headerName: '',
  field: m,
  headerComponent: 'ColumnIcon',
  cellRendererFramework: CountCellRenderer,
  cellStyle: iconCellStyle,
  flex: 0.12,
  minWidth: 30,
};

const F = {
  headerName: '',
  field: f,
  headerComponent: 'ColumnIcon',
  cellRendererFramework: CountCellRenderer,
  cellStyle: iconCellStyle,
  flex: 0.12,
  minWidth: 30,
};

const T = {
  headerName: '',
  field: t,
  headerComponent: 'ColumnIcon',
  cellRendererFramework: CountCellRenderer,
  cellStyle: iconCellStyle,
  flex: 0.12,
  minWidth: 30,
};

const N = {
  headerName: '',
  field: n,
  headerComponent: 'ColumnIcon',
  cellRendererFramework: CountCellRenderer,
  cellStyle: iconCellStyle,
  flex: 0.12,
  minWidth: 30,
};
const H = {
  headerName: h,
  field: h,
  headerComponent: 'ColumnIcon',
  cellRendererFramework: HCPCSCellRenderer,
  cellStyle: iconCellStyle,
  flex: 0.12,
  minWidth: 30,
};

export const frameworkComponents = {
  agDetailCellRenderer: VisitLogDetailRow,
  dotRenderer: DotRenderer,
  ColumnIcon: HeaderCellRenderer,
  countCellRenderer: CountCellRenderer,
};

export const getSmallColumnDefs = (patient: any) => {
  const conditionalHeaders = patient
    ? [VisitProviderSmall]
    : [
        VisitPatientLastNameSmall,
        VisitPatientFirstNameSmall,
        VisitPatientIDSmall,
      ];

  return [...conditionalHeaders, VisitDate, VisitType];
};

export const getReadOnlyColumnDefs = (patient: any) => {
  const conditionalHeaders = patient
    ? [VisitProviderLarge]
    : [
        VisitPatientLastNameLarge,
        VisitPatientFirstNameLarge,
        VisitPatientIDLarge,
      ];
  return [
    ...conditionalHeaders,
    VisitProviderPersonId,
    VisitProviderRoleName,
    VisitStatus,
    CreatedDate as any,
    VisitDate,
    VisitDuration,
    LateChart,
    VisitId,
    PrintF2F,
    RoleCode,
    TypeCode,
    LOCCode,
    LocationCode,
    VisitType,
    Hope,
    ElectionAddendum,
    ArriveDistance,
    DepartDistance,
    VisitAttributes,
    F2F,
    F,
    M,
    T,
    N,
    H,
  ];
};

export const getLargeColumnDefs = (patient: any) => [
  ...getReadOnlyColumnDefs(patient),
];
